import type {
  AgentCompactionConfig,
  AgentDefInfo,
  AgentScheduleEntry,
  AgentTemplate,
  CreateAgentDefDraftBody,
  CreateAgentTemplateBody,
  UpdateAgentDefBody,
} from '@/types/api'
import type { CompactionState, FormState, ScheduleEntry } from '@/types/editor'

// ── Defaults ──────────────────────────────────────────────────────────────────

export const DEFAULT_COMPACTION: CompactionState = {
  max_context_tokens: '',
  preserve_recent: '10',
  max_tool_result_tokens: '20000',
  summary_strategy: 'marker',
}

export const BLANK: FormState = {
  agent_id: '',
  name: '',
  model: '',
  provider_id: '',
  system_prompt: '',
  capabilities: 'tool:use:*\nllm:call:*',
  description: '',
  routing_hint: '',
  max_iterations: '',
  timeout_ms: '',
  max_tokens: '',
  max_cost_usd: '',
  session_ttl_days: '30',
  compaction: DEFAULT_COMPACTION,
  schedule: [],
}

// ── Parse helpers (def → form state) ─────────────────────────────────────────

function parseCompaction(v: unknown): CompactionState {
  if (!v || typeof v !== 'object') return DEFAULT_COMPACTION
  const r = v as Record<string, unknown>
  // Wire vocabulary is snake_case throughout (legacy camelCase rows were
  // rewritten by migration 0021).
  const summaryRaw = r.summary_strategy
  return {
    max_context_tokens:
      r.max_context_tokens != null ? String(r.max_context_tokens) : '',
    preserve_recent: String(r.preserve_recent ?? 10),
    max_tool_result_tokens: String(r.max_tool_result_tokens ?? 20000),
    summary_strategy:
      summaryRaw === 'llm_summary'
        ? 'llm_summary'
        : summaryRaw === 'episode_summary'
          ? 'episode_summary'
          : 'marker',
  }
}

function parseSchedule(v: unknown): ScheduleEntry[] {
  if (!Array.isArray(v)) return []
  return v.map((e) => {
    const r = (e ?? {}) as Record<string, unknown>
    return {
      schedule: String(r.schedule ?? ''),
      task: String(r.task ?? ''),
      // `activeHours` is the live wire key (serde rename_all = "camelCase" on
      // ScheduleConfigEntry) — the one camelCase field in this API. The key is
      // absent when no active-hours constraint is set (serde default).
      active_hours: String(r.activeHours ?? ''),
      timezone: String(r.timezone ?? ''),
    }
  })
}

export function fromDef(def: AgentDefInfo): FormState {
  return {
    agent_id: def.agent_id,
    name: def.name,
    model: def.model,
    provider_id: def.provider_id ?? '',
    system_prompt: def.system_prompt,
    capabilities: def.capabilities.join('\n'),
    description: def.description ?? '',
    routing_hint: def.routing_hint ?? '',
    max_iterations:
      def.max_iterations != null ? String(def.max_iterations) : '',
    timeout_ms: def.timeout_ms != null ? String(def.timeout_ms) : '',
    max_tokens: def.max_tokens != null ? String(def.max_tokens) : '',
    max_cost_usd: def.max_cost_usd != null ? String(def.max_cost_usd) : '',
    session_ttl_days: String(def.session_ttl_days),
    compaction: parseCompaction(def.compaction_config),
    schedule: parseSchedule(def.schedule_config),
    ...(def.reasoning ? { reasoning: def.reasoning } : {}),
  }
}

export function fromTemplate(t: AgentTemplate): FormState {
  return {
    agent_id: '',
    name: t.name,
    model: t.model,
    provider_id: t.provider_id ?? '',
    system_prompt: t.system_prompt,
    capabilities: t.capabilities.join('\n'),
    description: t.description ?? '',
    routing_hint: t.routing_hint ?? '',
    max_iterations: t.max_iterations != null ? String(t.max_iterations) : '',
    timeout_ms: t.timeout_ms != null ? String(t.timeout_ms) : '',
    max_tokens: '',
    max_cost_usd: '',
    session_ttl_days: String(t.session_ttl_days),
    compaction: parseCompaction(t.compaction_config),
    schedule: parseSchedule(t.schedule_config),
  }
}

// ── Serialize helpers (form state → JSON) ────────────────────────────────────

function serializeCompaction(s: CompactionState): AgentCompactionConfig {
  return {
    ...(s.max_context_tokens.trim()
      ? { max_context_tokens: Number(s.max_context_tokens) }
      : { max_context_tokens: null }),
    preserve_recent: Number(s.preserve_recent) || 10,
    max_tool_result_tokens: Number(s.max_tool_result_tokens) || 20000,
    summary_strategy: s.summary_strategy,
  }
}

function serializeSchedule(entries: ScheduleEntry[]): AgentScheduleEntry[] {
  return entries
    .filter((e) => e.schedule.trim() && e.task.trim())
    .map((e) => ({
      schedule: e.schedule.trim(),
      task: e.task,
      activeHours: e.active_hours.trim() || null,
      timezone: e.timezone.trim() || null,
    }))
}

// ── Body builders ─────────────────────────────────────────────────────────────

export function toCreateBody(f: FormState): CreateAgentDefDraftBody {
  const capabilities = f.capabilities
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  return {
    ...(f.agent_id.trim() ? { agent_id: f.agent_id.trim() } : {}),
    name: f.name.trim(),
    model: f.model.trim(),
    provider_id: f.provider_id,
    system_prompt: f.system_prompt,
    capabilities,
    ...(f.max_iterations.trim()
      ? { max_iterations: Number(f.max_iterations) }
      : {}),
    ...(f.timeout_ms.trim() ? { timeout_ms: Number(f.timeout_ms) } : {}),
    ...(f.max_tokens.trim() ? { max_tokens: Number(f.max_tokens) } : {}),
    ...(f.max_cost_usd.trim() ? { max_cost_usd: Number(f.max_cost_usd) } : {}),
    session_ttl_days: Number(f.session_ttl_days) || 30,
    ...(f.description.trim() ? { description: f.description.trim() } : {}),
    ...(f.routing_hint.trim() ? { routing_hint: f.routing_hint.trim() } : {}),
    compaction_config: serializeCompaction(f.compaction),
    schedule_config: serializeSchedule(f.schedule),
    ...(f.reasoning ? { reasoning: f.reasoning } : {}),
  }
}

export function toTemplateBody(f: FormState): CreateAgentTemplateBody {
  const capabilities = f.capabilities
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  return {
    name: f.name.trim(),
    model: f.model.trim(),
    ...(f.provider_id.trim() ? { provider_id: f.provider_id.trim() } : {}),
    system_prompt: f.system_prompt,
    capabilities,
    ...(f.max_iterations.trim()
      ? { max_iterations: Number(f.max_iterations) }
      : {}),
    ...(f.timeout_ms.trim() ? { timeout_ms: Number(f.timeout_ms) } : {}),
    session_ttl_days: Number(f.session_ttl_days) || 30,
    ...(f.description.trim() ? { description: f.description.trim() } : {}),
    ...(f.routing_hint.trim() ? { routing_hint: f.routing_hint.trim() } : {}),
    compaction_config: serializeCompaction(f.compaction),
    schedule_config: serializeSchedule(f.schedule),
  }
}

export function toUpdateBody(
  f: FormState,
  stored?: FormState['reasoning'],
): UpdateAgentDefBody {
  const capabilities = f.capabilities
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  // A null is sent only to clear a stored setting: with none stored it would
  // mark an unmoved thinking_config done and drop its carry-over.
  const sendReasoning =
    f.reasoning != null || (f.reasoning === null && stored != null)
  return {
    name: f.name.trim(),
    model: f.model.trim(),
    provider_id: f.provider_id || undefined,
    system_prompt: f.system_prompt,
    capabilities,
    max_iterations: f.max_iterations.trim() ? Number(f.max_iterations) : null,
    timeout_ms: f.timeout_ms.trim() ? Number(f.timeout_ms) : null,
    max_tokens: f.max_tokens.trim() ? Number(f.max_tokens) : null,
    max_cost_usd: f.max_cost_usd.trim() ? Number(f.max_cost_usd) : null,
    session_ttl_days: Number(f.session_ttl_days) || 30,
    description: f.description.trim(),
    routing_hint: f.routing_hint.trim(),
    compaction_config: serializeCompaction(f.compaction),
    schedule_config: serializeSchedule(f.schedule),
    ...(sendReasoning ? { reasoning: f.reasoning } : {}),
  }
}
